import { getModel } from '../../model/registry'
import styled from "styled-components";

import { useEffect, useState, memo, useRef } from "react";
import throttle from 'lodash/throttle';
import useApi from "../../hooks/useApi";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { portalState } from "../../hooks/usePortal";
import { useSnapshot } from "valtio";

import Loader from "../generic/Loader";
import PdfView from "./PdfView";
import PdfErrorBoundary from "./PdfErrorBoundary";


const msg = {
  "localLang": "da-DK",
  "fileName": "pdf",
}

const PdfContent = styled.div`

  width: 100%;
  @media (max-width: 768px) {
    background : ${({ theme }) => theme.pdf.background};
    padding: 0.75rem;
  } 
`

const PdfWrapper = styled.div`
    width: 100%;
    height: ${(props) => `${props.height}px` || '100px'};

`

const PdfPager = memo(({ state, entity }) => {
  const [width, setWidth] = useState(200);
  const queryClient = useQueryClient();
  const pdfWrapperRef = useRef(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const portalSnap = useSnapshot(portalState);

  const config = getModel('pdfData')[entity] || { images: [] };

  useEffect(() => {
    isMountedRef.current = true;
    
    const updateWidth = () => {
      if (!isMountedRef.current) return;
      
      // Use ref instead of getElementById for better reliability
      if (pdfWrapperRef.current) {
        const newWidth = pdfWrapperRef.current.getBoundingClientRect().width;
        setWidth(newWidth);
      }
    };

    const handlePdfContentSizeChange = throttle(() => {
      updateWidth();
    }, 1000);

    // Store throttled function references for proper cleanup
    const throttledResizeHandler = throttle(updateWidth, 1500);

    // Initial call to set the width
    updateWidth();

    // Add resize event listener
    window.addEventListener("resize", throttledResizeHandler);

    const resizeObserver = new ResizeObserver(handlePdfContentSizeChange);
    if (pdfWrapperRef.current) {
      resizeObserver.observe(pdfWrapperRef.current);
    }

    // Cleanup: remove event listener and disconnect observer
    return () => {
      isMountedRef.current = false;
      window.removeEventListener("resize", throttledResizeHandler);
      resizeObserver.disconnect();
      
      // Cancel any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const { postMeStream } = useApi();

  const getPdf = async ({ signal }) => {
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const payload = { data: state, msg: msg, images: config.images };
      const endpoint = portalSnap.portalId ? 'portal/pdf' : 'pdf';
      const response = await postMeStream(endpoint, payload);
      return response;
    } catch (error) {
      // Don't throw if the request was aborted due to component unmounting
      if (error.name === 'AbortError' && !isMountedRef.current) {
        return null;
      }
      throw error;
    }
  };

const result = useQuery({
  queryKey: ['pdf',`${state.savedOn}`, entity],
  queryFn: getPdf,
  staleTime: 10000,
  retry: (failureCount, error) => {
    // Don't retry if component is unmounted or request was aborted
    if (!isMountedRef.current || error?.name === 'AbortError') {
      return false;
    }
    return failureCount < 1;
  },
  refetchOnWindowFocus: false,
  enabled: !!state.savedOn, // Only run query if we have savedOn
});

  // Use the query result directly instead of separate state
  const pdf = result.data;

  // Show loading state
  if (result.isLoading) {
    return (
      <PdfContent className="contentPdf">
        <PdfWrapper ref={pdfWrapperRef} id="pdfWrapper" height={width * 1.41 + 65}>
          <Loader />
        </PdfWrapper>
      </PdfContent>
    );
  }

  // Show error state
  if (result.isError) {
    return (
      <PdfContent className="contentPdf">
        <PdfWrapper ref={pdfWrapperRef} id="pdfWrapper" height={width * 1.41 + 65}>
          <div>Error generating PDF: {result.error?.message || 'Unknown error'}</div>
        </PdfWrapper>
      </PdfContent>
    );
  }

  return (
    <PdfContent className="contentPdf" >
      <PdfWrapper ref={pdfWrapperRef} id="pdfWrapper" height={width * 1.41 + 65}>
        {pdf ? (
          <PdfErrorBoundary>
            <PdfView id="pdfView" pdf={pdf} width={width} state={state} />
          </PdfErrorBoundary>
        ) : (
          <div>No PDF data available</div>
        )}
      </PdfWrapper>
    </PdfContent>
  );
}, (prevProps, nextProps) => {
  return prevProps.state.savedOn === nextProps.state.savedOn && prevProps.entity === nextProps.entity;
});

export default PdfPager;
